import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import Tabs from "src/components/ui/Tabs";
import RolesAndPermissionsTab from "src/components/cards/RolesAndPermissionsTab";
import TenantUserListTable from "src/components/tables/TenantUserListTable";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantUserActions from "src/redux/slices/tenantUserSlice";
import { toast } from "react-toastify";
import usePlanUsage from "src/hooks/usePlanUsage";

const TeamManagementPage: React.FC = () => {
  const basePath = "/app/team-management";
  const location = useLocation();
  const navigate = useNavigate();
  const [limit] = useState(10);
  const [statusUpdate, setStatusUpdate] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRow, setStatusRow] = useState<any | null>(null);

  const { tenantUserList, editTenantUser } = useAppSelector(
    (state) => state.tenantUser
  );
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = useMemo(
    () => user?.roleDetails?.permissions || user?.permissions || {},
    [user?.roleDetails?.permissions, user?.permissions]
  );
  const permissionEntries = Object.keys(permissions || {});
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canAccessTeamManagement =
    isTenantAdmin ||
    permissionEntries.length === 0 ||
    (permissions?.users || []).includes("view");
  const canManageRoles = isTenantAdmin;

  const tabs = useMemo(() => {
    const items = [{ label: "Users", value: "users" }];
    if (canManageRoles) {
      items.push({ label: "Roles", value: "roles" });
    }
    return items;
  }, [canManageRoles]);
  const dispatch = useAppDispatch();
  const { planUsage } = usePlanUsage();

  const userLimit = planUsage?.plan?.features?.totalUsers;
  const userUsage = planUsage?.usage?.totalUsers;
  const isPlanExpired = planUsage?.planStatus === "expired";
  const isUserLimitReached =
    typeof userLimit === "number" &&
    typeof userUsage === "number" &&
    userUsage >= userLimit;
  const disableCreateUser = isPlanExpired || isUserLimitReached;

  const canCreateUser =
    isTenantAdmin || (permissions?.users || []).includes("create");
  const canEditUser =
    isTenantAdmin || (permissions?.users || []).includes("edit");
  const canToggleUser =
    isTenantAdmin || (permissions?.users || []).includes("delete");

  const getTenantUsers = useCallback(
    (page: number) => {
      dispatch(
        tenantUserActions.getTenantUserListRequest({
          page,
          limit,
        })
      );
    },
    [dispatch, limit]
  );

  const isRolePath = location.pathname.startsWith(`${basePath}/role`);
  const isUserPath = location.pathname.startsWith(`${basePath}/user`);
  const activeTab = isRolePath ? "roles" : "users";

  useEffect(() => {
    if (!isUserPath) return;
    getTenantUsers(1);
  }, [isUserPath, getTenantUsers]);

  useEffect(() => {
    if (editTenantUser.data && statusUpdate) {
      setStatusUpdate(false);
      toast.success("User modified successfully");
      dispatch(tenantUserActions.resetEditTenantUser());
      handleStatusModalClose();
      getTenantUsers(1);
    } else if (editTenantUser.error && statusUpdate) {
      toast.error(`Error: ${editTenantUser.error}`);
      setStatusUpdate(false);
      handleStatusModalClose();
    }
  }, [editTenantUser, statusUpdate, dispatch, getTenantUsers]);

  const handleEdit = (data: any) => {
    navigate(`${basePath}/user/${data._id}/edit`, {
      state: {
        user: {
          ...data,
          role: data?.role?._id || data?.role,
        },
      },
    });
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusRow(null);
  };

  const onConfirmStatus = () => {
    if (!statusRow?._id) return;
    setStatusUpdate(true);
    dispatch(
      tenantUserActions.editTenantUserRequest({
        _id: statusRow._id,
        status: statusRow.status === "active" ? "suspended" : "active",
      })
    );
  };

  const handlePageChange = (page: number) => getTenantUsers(page);

  if (!canAccessTeamManagement) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (location.pathname === basePath) {
    return <Navigate to={`${basePath}/user-list`} replace />;
  }

  if (isRolePath && !canManageRoles) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <DashboardLayout
      meta={{
        title: "Team management | Invyntra",
        description: "Manage your team members and roles in one place.",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Team management"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            {
              label: "Team management",
              path: `${basePath}/${activeTab === "roles" ? "role-list" : "user-list"}`,
            },
          ]}
        />

        <Card>
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={(value) =>
              navigate(
                `${basePath}/${value === "roles" ? "role-list" : "user-list"}`
              )
            }
            className="flex gap-2 border-b border-gray-200"
          />

          <div className="pt-4">
            {activeTab === "users" && (
              <>
                <CardHeader
                  title="Users"
                  description="Manage all tenant users here"
                  actions={
                    canCreateUser ? (
                      <Button
                        onClick={() => navigate(`${basePath}/user/create`)}
                        disabled={disableCreateUser}
                        title={
                          isPlanExpired
                            ? "Plan expired. Please upgrade."
                            : isUserLimitReached
                            ? "User limit reached. Please upgrade."
                            : undefined
                        }
                      >
                        <Plus size={16} className="mr-1" /> Add User
                      </Button>
                    ) : null
                  }
                />

                <TenantUserListTable
                  data={tenantUserList?.data?.data || []}
                  pagination={tenantUserList?.data?.pagination}
                  onPageChange={handlePageChange}
                  onEdit={handleEdit}
                  onToggleStatus={(row) => {
                    setStatusRow(row);
                    setShowStatusModal(true);
                  }}
                  canEdit={canEditUser}
                  canToggle={canToggleUser}
                />
              </>
            )}

            {activeTab === "roles" && (
              <>
                <CardHeader
                  title="Roles"
                  description="Manage roles and permissions for your team"
                />
                <RolesAndPermissionsTab basePath={basePath} />
              </>
            )}
          </div>
        </Card>
      </div>

      {showStatusModal && statusRow?._id && (
        <ConfirmationModal
          message={`Are you sure to ${
            statusRow.status === "active" ? "suspend" : "activate"
          } this user?`}
          onConfirm={onConfirmStatus}
          onCancel={handleStatusModalClose}
          open={showStatusModal}
          isLoading={editTenantUser.loading}
        />
      )}
    </DashboardLayout>
  );
};

export default TeamManagementPage;
